import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { getDealById, getCurrentPIFSubmission, getAnalystReviews } from '../../services/dealService';
import { getAllApprovalSequences } from '../../services/firestoreService';
import PortalLayout from '../../components/shared/Layout/PortalLayout';
import HOPReviewForm from '../../components/forms/HOPReviewForm';
import PIFScoreCard from '../../components/pipeline/PIFScoreCard';
import AISummaryPanel from '../../components/pipeline/AISummaryPanel';
import FlagPanel from '../../components/pipeline/FlagPanel';
import Button from '../../components/shared/UI/Button';
import LoadingSpinner from '../../components/shared/UI/LoadingSpinner';
import { formatCurrency } from '../../utils/formatters';

export default function HOPReviewPage() {
  const { dealId } = useParams();
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [deal, setDeal] = useState(null);
  const [submission, setSubmission] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [approvalSequence, setApprovalSequence] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      const d = await getDealById(dealId);
      if (!d) { setLoading(false); return; }
      setDeal(d);
      const [s, revs, sequences] = await Promise.all([
        getCurrentPIFSubmission(dealId),
        getAnalystReviews(dealId),
        getAllApprovalSequences(),
      ]);
      setSubmission(s);
      setReviews(revs);
      setApprovalSequence(sequences.find(seq => seq.fund_id === d.fund_id));
      setLoading(false);
    }
    load();
  }, [dealId]);

  if (loading) return <PortalLayout><LoadingSpinner /></PortalLayout>;
  if (!deal) return <PortalLayout><div className="alert alert-error">Deal not found.</div></PortalLayout>;

  if (!(userProfile?.roles || [userProfile?.role]).includes('head_of_pipeline')) {
    return (
      <PortalLayout>
        <div className="alert alert-error">Only the Head of Pipeline can complete this review.</div>
      </PortalLayout>
    );
  }

  const primaryReview = reviews.find(r => r.review_type === 'primary_analyst');
  const peerReview = reviews.find(r => r.review_type === 'peer_review');

  return (
    <PortalLayout>
      <div style={{ marginBottom: 16 }}>
        <Button variant="ghost" size="sm" onClick={() => navigate(`/pipeline/${dealId}`)}>← {deal.company_name}</Button>
      </div>
      <h1 style={{ fontSize: 20, fontWeight: 700, marginBottom: 6 }}>Head of Pipeline Review</h1>
      <p style={{ fontSize: 14, color: 'var(--color-text-secondary)', marginBottom: 24 }}>{deal.company_name}</p>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 24 }}>
        {/* Left: Summary panel */}
        <div>
          <div className="card mb-4">
            <div className="card-header"><h2>Deal Summary</h2></div>
            <div className="card-body">
              {[
                ['Company', deal.company_name],
                ['Sector', deal.sector],
                ['Country', deal.company_country],
                ['Amount', formatCurrency(deal.requested_amount, deal.requested_currency)],
              ].map(([l, v]) => v ? (
                <div key={l} style={{ display: 'flex', gap: 10, marginBottom: 6 }}>
                  <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-secondary)', minWidth: 80 }}>{l}</p>
                  <p style={{ fontSize: 13 }}>{v}</p>
                </div>
              ) : null)}
            </div>
          </div>
          <div className="mb-4"><PIFScoreCard submission={submission} rubric={null} /></div>
          {submission && <div className="mb-4"><AISummaryPanel submission={submission} dealId={dealId} /></div>}
          <FlagPanel reviews={reviews} />
          {primaryReview && (
            <div className="card mt-3">
              <div className="card-header"><h2>Analyst Notes</h2></div>
              <div className="card-body"><p style={{ fontSize: 14, lineHeight: 1.7 }}>{primaryReview.notes}</p></div>
            </div>
          )}
          {peerReview && (
            <div className="card mt-3">
              <div className="card-header"><h2>Peer Review Notes</h2></div>
              <div className="card-body"><p style={{ fontSize: 14, lineHeight: 1.7 }}>{peerReview.notes}</p></div>
            </div>
          )}
        </div>

        {/* Right: Review form */}
        <HOPReviewForm
          deal={deal}
          approvalSequence={approvalSequence}
          onSaved={() => navigate(`/pipeline/${dealId}`)}
        />
      </div>
    </PortalLayout>
  );
}
